import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { Series } from './api';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function TrendFigures({
  series,
  selected,
  onSelect,
}: {
  series: Series[];
  selected?: string[];
  onSelect?: (keys: string[]) => void;
}) {
  if (!series.length)
    return (
      <Alert>
        <AlertDescription>No reviewed measurements are available for this pregnancy.</AlertDescription>
      </Alert>
    );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {series.map((s) => (
        <section key={s.key} className="min-w-0 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3>
              {s.fetus ? `Fetus ${s.fetus} · ` : ''}
              {s.metric} ({s.unit})
            </h3>
            {onSelect && (
              <Label className="flex items-center gap-2">
                <Checkbox
                  checked={selected?.includes(s.key)}
                  onCheckedChange={(v) =>
                    onSelect(v ? [...(selected || []), s.key] : (selected || []).filter((k) => k !== s.key))
                  }
                />
                Include in report
              </Label>
            )}
          </div>
          <div role="img" className="h-56" aria-label={`${s.metric} over gestational age`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                margin={{ top: 10, left: 10, right: 25, bottom: 20 }}
                data={s.points.filter((p) => p.ga_days !== null).map((p) => ({ ...p, weeks: p.ga_days! / 7 }))}>
                <CartesianGrid stroke="var(--border)" />
                <XAxis
                  type="number"
                  dataKey="weeks"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(v) => Number(v).toFixed(1)}
                  label={{ position: 'bottom', value: 'Gestational age (weeks)' }}
                />
                <YAxis width={65} domain={['auto', 'auto']} tickFormatter={(v) => Number(v).toFixed(1)} />
                <Tooltip
                  labelFormatter={(v) => `${Number(v).toFixed(2)} weeks`}
                  formatter={(v, name) => [`${Number(v).toFixed(2)} ${s.unit}`, name === "value" ? "Observed" : name]}
                />
                {s.reference && <><Line dot={false} name="−2 SD" type="linear" dataKey="lower" strokeDasharray="3 3" isAnimationActive={false} stroke="var(--muted-foreground)"/><Line dot={false} type="linear" dataKey="median" name="Reference median" isAnimationActive={false} stroke="var(--muted-foreground)"/><Line dot={false} name="+2 SD" type="linear" dataKey="upper" strokeDasharray="3 3" isAnimationActive={false} stroke="var(--muted-foreground)"/></>}
                <Line dot type="linear" dataKey="value" stroke="var(--primary)" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {s.reference && <p>{s.reference} · median (solid) and ±2 SD (dashed)</p>}
          <details>
            <summary>Source values and studies</summary>
            <ul>
              {s.points.map((p) => (
                <li key={p.study_id}>
                  {p.date}: {p.value.toFixed(2)} {s.unit} ·{' '}
                  {p.ga_days === null ? 'Dating unavailable' : `${Math.floor(p.ga_days / 7)}w ${p.ga_days % 7}d`} ·{' '}
                  {p.study_id}
                  {p.formula ? ` · ${p.formula}` : ''}
                </li>
              ))}
            </ul>
          </details>
        </section>
      ))}
    </div>
  );
}
